/**
 * Phase 8 · Cluster C-48 — Agent write tools.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §34.2.C
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §10.2
 *
 * Eleven write-capable tools (`capability: 'write'`) that the runner can
 * invoke AFTER the user approves a Plan card — or, for a single
 * reversible + off-safety-list tool with autonomous mode ON, directly.
 *
 * Each handler must:
 *   1. Validate its args via zod (argsSchema is enforced by the registry
 *      before `invoke()` is called, but we also defensively re-assert
 *      anything that couldn't be expressed in a simple schema, e.g.
 *      non-empty string lists after trimming).
 *   2. Assert `org_id === ctx.orgId`. The underlying *Service methods
 *      accept an explicit `orgId` and their SQL is `.eq('org_id', …)` —
 *      RLS enforces the same condition server-side, so this check is
 *      mostly to fail fast on obviously malformed args.
 *   3. Delegate to the matching *Service rather than hitting supabase
 *      directly. No business logic is reimplemented here: the existing
 *      services already do validation, status-history writes, pipeline
 *      locks, and the outbox-trigger fallback. Our tools are thin
 *      adapters that translate an `AgentTool.invoke()` call into the
 *      service's native shape.
 *   4. Return an `AgentWriteToolResult` with `{ ok, entityKind,
 *      entityId, entityLabel, reversible }` at minimum. The runner
 *      consumes these fields to render step cards in the Plan card UI
 *      and to compute the Reversible/Permanent badge.
 *
 * The `reversible` flag mirrors the spec's autonomous-mode rules:
 *   - true for intra-org status/notes transitions that don't leave
 *     the system of record (drafts, internal updates).
 *   - false for anything that sends email, advances requisitions,
 *     creates billable entities, or rejects a candidate — these are
 *     also on the hardcoded safety list (src/modules/agent/safety.ts)
 *     so even with autonomous mode ON they always need approval.
 *
 * Registration:
 *   This module runs at app boot via `src/modules/agent/tools/index.ts`
 *   (imported from `src/main.tsx`). Each call below registers one tool
 *   and wires its handler into `toolRegistry.dispatch`.
 */
import { z } from 'zod';
import { toolRegistry, type AgentWriteToolResult } from './registry';
import { requisitionService } from '@core/supabase/services/requisitionService';
import { candidateService } from '@core/supabase/services/candidateService';
import { applicationService } from '@core/supabase/services/applicationService';
import { emailService } from '@core/supabase/services/emailService';
import { interviewEventService } from '@core/supabase/services/interviewEventService';
import { scorecardService } from '@core/supabase/services/scorecardService';
import { emailSequenceService } from '@core/supabase/services/emailSequenceService';
import { sequenceRecipientsService } from '@core/supabase/services/sequenceRecipientsService';
import { REQUISITION_STATUSES, APPLICATION_STATUSES } from '@core/enums';
import type { RequisitionStatus, ApplicationStatus, RequisitionInsert, Candidate } from '@core/types';

// ─────────────────────────────────────────────────────────────────────
// Shared zod helpers + result envelope
// ─────────────────────────────────────────────────────────────────────

/**
 * Every write tool returns a variant of this envelope. We keep the shape
 * uniform so the Plan card / step status strip can render without
 * knowing which tool produced the row.
 */
const writeResultSchema = z.object({
  ok: z.boolean(),
  entityKind: z.enum([
    'requisition',
    'candidate',
    'application',
    'interview',
    'email',
    'sequence',
    'shortlist',
    'outreach',
  ]),
  entityId: z.string(),
  entityLabel: z.string(),
  reversible: z.boolean(),
  message: z.string().optional(),
  detail: z.record(z.unknown()).optional(),
});

/** Throws a clear error when the args carry a different org than the runner ctx. */
function assertOrg(argsOrgId: string | undefined, ctxOrgId: string, toolName: string): void {
  if (!ctxOrgId) {
    throw new Error(`[${toolName}] ctx.orgId is required — runner must populate AgentToolCtx.`);
  }
  if (argsOrgId != null && argsOrgId !== ctxOrgId) {
    // Defence in depth. RLS will reject the write anyway, but failing here
    // gives the user a readable error in the step result card instead of
    // a cryptic Postgres message.
    throw new Error(
      `[${toolName}] args.orgId does not match the signed-in org; refusing to dispatch.`,
    );
  }
}

// Short, human-readable identifier for the step card. We only surface
// the first 8 chars of a UUID so the Plan row stays readable.
function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

// ─────────────────────────────────────────────────────────────────────
// write.requisitions.create
// ─────────────────────────────────────────────────────────────────────

const createRequisitionArgs = z.object({
  clientId: z.string().min(1),
  title: z.string().min(3),
  headcount: z.number().int().positive().max(99).default(1),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
  department: z.string().optional(),
  hiringManagerName: z.string().optional(),
  hiringManagerEmail: z.string().email().optional(),
  location: z.string().optional(),
  employmentType: z.string().optional(),
  budgetCurrency: z.string().default('INR'),
  budgetMin: z.number().positive().optional(),
  budgetMax: z.number().positive().optional(),
  mustHaveSkills: z.array(z.string()).default([]),
  niceToHaveSkills: z.array(z.string()).default([]),
  internalNotes: z.string().optional(),
});

toolRegistry.register<z.infer<typeof createRequisitionArgs>, AgentWriteToolResult>({
  name: 'write.requisitions.create',
  description:
    'Create a new requisition (draft) under the given client. Minimum args: clientId + title. Extra fields (priority, headcount, budget, skills, hiring manager) populate if provided. Status defaults to "draft" — use write.requisitions.advance_stage to progress it.',
  argsSchema: createRequisitionArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.requisitions.create');
    const payload: RequisitionInsert = {
      org_id: ctx.orgId,
      client_id: args.clientId,
      created_by: ctx.userId,
      title: args.title.trim(),
      department: args.department ?? null,
      headcount: args.headcount,
      priority: args.priority,
      status: 'draft' as RequisitionStatus,
      hiring_manager_name: args.hiringManagerName ?? null,
      hiring_manager_email: args.hiringManagerEmail ?? null,
      budget_currency: args.budgetCurrency,
      budget_min: args.budgetMin ?? null,
      budget_max: args.budgetMax ?? null,
      location: args.location ?? null,
      employment_type: args.employmentType ?? null,
      business_justification: null,
      must_have_skills: args.mustHaveSkills,
      nice_to_have_skills: args.niceToHaveSkills,
      internal_notes: args.internalNotes ?? null,
      target_start_date: null,
      submitted_at: null,
      approved_at: null,
      rejected_at: null,
      opened_at: null,
      target_close_date: null,
      closed_at: null,
      approver_user_id: null,
      approval_notes: null,
      created_job_id: null,
      job_id: null,
    } as RequisitionInsert;
    const row = await requisitionService.create(payload);
    return {
      ok: true,
      entityKind: 'requisition',
      entityId: row.id,
      entityLabel: row.title,
      // Creates are NOT reversible in autonomous mode — they're on the
      // hardcoded safety list regardless (safety.ts) because
      // requisitions are billable/regulated per §34.2.C.
      reversible: false,
      message: `Requisition "${row.title}" created (${shortId(row.id)}) as draft.`,
      detail: { requisitionId: row.id, clientId: args.clientId, status: row.status },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.requisitions.update
// ─────────────────────────────────────────────────────────────────────

const updateRequisitionArgs = z.object({
  requisitionId: z.string().min(1),
  patch: z
    .object({
      title: z.string().min(3).optional(),
      department: z.string().nullable().optional(),
      headcount: z.number().int().positive().max(99).optional(),
      priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
      hiring_manager_name: z.string().nullable().optional(),
      hiring_manager_email: z.string().email().nullable().optional(),
      location: z.string().nullable().optional(),
      employment_type: z.string().nullable().optional(),
      budget_currency: z.string().optional(),
      budget_min: z.number().positive().nullable().optional(),
      budget_max: z.number().positive().nullable().optional(),
      must_have_skills: z.array(z.string()).optional(),
      nice_to_have_skills: z.array(z.string()).optional(),
      internal_notes: z.string().nullable().optional(),
      target_start_date: z.string().nullable().optional(),
    })
    .refine((v) => Object.keys(v).length > 0, { message: 'patch must contain at least one field' }),
});

toolRegistry.register<z.infer<typeof updateRequisitionArgs>, AgentWriteToolResult>({
  name: 'write.requisitions.update',
  description:
    "Update a draft requisition's editable fields (title / priority / budget / skills / hiring manager etc.). Does NOT change status — use write.requisitions.advance_stage for that.",
  argsSchema: updateRequisitionArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.requisitions.update');
    const row = await requisitionService.update(args.requisitionId, args.patch);
    return {
      ok: true,
      entityKind: 'requisition',
      entityId: row.id,
      entityLabel: row.title,
      // Editable-field updates are reversible: the previous value can be
      // restored via another update. Still goes through the plan card
      // for multi-step plans; for a single call with autonomous mode ON
      // the runner can auto-execute.
      reversible: true,
      message: `Requisition "${row.title}" updated.`,
      detail: { requisitionId: row.id, patched: Object.keys(args.patch) },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.requisitions.advance_stage
// ─────────────────────────────────────────────────────────────────────

// Canonical lifecycle:
//   draft → awaiting_client → submitted → approved → sourcing → filled
// 'on_hold' / 'cancelled' / 'rejected' are side states the agent should
// not land via `advance`; we force the caller to name the next status so
// mis-planning can't accidentally skip approval.
const advanceRequisitionArgs = z.object({
  requisitionId: z.string().min(1),
  nextStatus: z.enum(REQUISITION_STATUSES),
  notes: z.string().optional(),
});

toolRegistry.register<z.infer<typeof advanceRequisitionArgs>, AgentWriteToolResult>({
  name: 'write.requisitions.advance_stage',
  description:
    "Transition a requisition to the next lifecycle stage (draft → awaiting_client → submitted → approved → sourcing → filled). Caller must name the target status explicitly so the agent can't advance accidentally.",
  argsSchema: advanceRequisitionArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.requisitions.advance_stage');
    const patch: Partial<Awaited<ReturnType<typeof requisitionService.update>>> = {
      status: args.nextStatus,
    };
    if (args.notes && args.nextStatus === 'approved') {
      patch.approval_notes = args.notes;
    }
    const row = await requisitionService.update(args.requisitionId, patch);
    return {
      ok: true,
      entityKind: 'requisition',
      entityId: row.id,
      entityLabel: row.title,
      // Stage advancement is NOT reversible in autonomous mode — on the
      // hardcoded safety list. Manual approve is always required.
      reversible: false,
      message: `Requisition "${row.title}" → ${row.status}.`,
      detail: { requisitionId: row.id, newStatus: row.status, notes: args.notes ?? null },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.candidates.create
// ─────────────────────────────────────────────────────────────────────

const createCandidateArgs = z.object({
  fullName: z.string().min(2),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  currentTitle: z.string().optional(),
  currentCompany: z.string().optional(),
  location: z.string().optional(),
  linkedinUrl: z.string().url().optional(),
  skills: z.array(z.string()).default([]),
  yearsExp: z.number().min(0).max(60).optional(),
  notes: z.string().optional(),
});

toolRegistry.register<z.infer<typeof createCandidateArgs>, AgentWriteToolResult>({
  name: 'write.candidates.create',
  description:
    'Add a candidate to the org pool. email is preferred but not required (some connectors return anonymous profiles). Skills are stored as parsed_skills. Returns the new candidate id so follow-up steps (shortlist, outreach) can reference it.',
  argsSchema: createCandidateArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.candidates.create');
    const insertPayload: Omit<Candidate, 'id' | 'created_at' | 'updated_at'> = {
      org_id: ctx.orgId,
      created_by: ctx.userId,
      full_name: args.fullName.trim(),
      email: args.email ?? '',
      phone: args.phone ?? null,
      linkedin_url: args.linkedinUrl ?? null,
      source_channel: 'agent',
      source: 'manual',
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      current_company: args.currentCompany ?? null,
      current_title: args.currentTitle ?? null,
      location: args.location ?? null,
      avatar_url: null,
      base_resume_url: null,
      recruiter_rating: null,
      parsed_skills: args.skills,
      education: null,
      tags: [],
      notes: args.notes ?? null,
      is_active: true,
      years_exp: args.yearsExp ?? null,
    };
    const row = await candidateService.create(insertPayload);
    return {
      ok: true,
      entityKind: 'candidate',
      entityId: row.id,
      entityLabel: row.full_name,
      // A freshly created candidate can be soft-deleted (is_active=false)
      // without side effects — treat as reversible for autonomous mode
      // decisions. Safety list still applies if a plan has multiple steps.
      reversible: true,
      message: `Candidate "${row.full_name}" added (${shortId(row.id)}).`,
      detail: { candidateId: row.id, email: row.email || null },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.candidates.shortlist
// ─────────────────────────────────────────────────────────────────────

const shortlistCandidatesArgs = z.object({
  jobId: z.string().min(1),
  clientId: z.string().min(1),
  candidateIds: z.array(z.string().min(1)).min(1).max(50),
  note: z.string().optional(),
});

toolRegistry.register<z.infer<typeof shortlistCandidatesArgs>, AgentWriteToolResult>({
  name: 'write.candidates.shortlist',
  description:
    "Bulk-shortlist a set of candidates against a job's pipeline. For each candidate this creates an application row (if absent) and moves it to status='shortlisted'. Capped at 50 candidates per call.",
  argsSchema: shortlistCandidatesArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.candidates.shortlist');
    let created = 0;
    let advanced = 0;
    const failed: string[] = [];
    for (const candidateId of args.candidateIds) {
      try {
        let app = await applicationService.findByCandidateAndJob(ctx.orgId, candidateId, args.jobId);
        if (!app) {
          app = await applicationService.create({
            org_id: ctx.orgId,
            candidate_id: candidateId,
            job_id: args.jobId,
            client_id: args.clientId,
            created_by: ctx.userId,
          });
          created += 1;
        }
        // Move to shortlisted if it isn't already there. updateStatus
        // writes an application_status_history row with our note.
        if (app.status !== 'shortlisted') {
          await applicationService.updateStatus(app.id, 'shortlisted', args.note);
          advanced += 1;
        }
      } catch (err) {
        failed.push(candidateId);
        console.warn('[write.candidates.shortlist] failed for', candidateId, err);
      }
    }
    const total = args.candidateIds.length;
    return {
      ok: failed.length === 0,
      entityKind: 'shortlist',
      entityId: args.jobId,
      entityLabel: `${total} candidates → shortlist`,
      // Shortlisting is reversible (status can be moved back) but the
      // audit trail remains. Safety list doesn't include shortlist, so
      // autonomous mode can auto-execute a single call.
      reversible: true,
      message:
        failed.length === 0
          ? `Shortlisted ${total} candidates (${created} new, ${advanced} advanced).`
          : `Shortlisted ${total - failed.length}/${total} candidates — ${failed.length} failed.`,
      detail: {
        jobId: args.jobId,
        created,
        advanced,
        failed,
      },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.applications.advance
// ─────────────────────────────────────────────────────────────────────

const advanceApplicationArgs = z.object({
  applicationId: z.string().min(1),
  nextStatus: z.enum(APPLICATION_STATUSES),
  note: z.string().optional(),
});

toolRegistry.register<z.infer<typeof advanceApplicationArgs>, AgentWriteToolResult>({
  name: 'write.applications.advance',
  description:
    'Transition an application to the next stage (any ApplicationStatus). Use write.applications.reject for "rejected" — it carries extra side effects and safety gating.',
  argsSchema: advanceApplicationArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.applications.advance');
    if (args.nextStatus === 'rejected' || args.nextStatus === 'offer_rejected') {
      throw new Error(
        "Use write.applications.reject for rejections — it enforces the rejection-reason contract.",
      );
    }
    const { updated_at } = await applicationService.updateStatus(
      args.applicationId,
      args.nextStatus as ApplicationStatus,
      args.note,
    );
    return {
      ok: true,
      entityKind: 'application',
      entityId: args.applicationId,
      entityLabel: `Application → ${args.nextStatus}`,
      reversible: true, // status can be reverted; no external side effects here
      message: `Application ${shortId(args.applicationId)} advanced to ${args.nextStatus}.`,
      detail: { applicationId: args.applicationId, status: args.nextStatus, updated_at },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.applications.reject
// ─────────────────────────────────────────────────────────────────────

const rejectApplicationArgs = z.object({
  applicationId: z.string().min(1),
  reasonCode: z.enum([
    'not_qualified',
    'overqualified',
    'salary_mismatch',
    'no_show',
    'position_filled',
    'other',
  ]),
  reasonText: z.string().min(4),
});

toolRegistry.register<z.infer<typeof rejectApplicationArgs>, AgentWriteToolResult>({
  name: 'write.applications.reject',
  description:
    "Reject an application with a structured reason. Writes rejection_reason_code / rejection_reason_text and moves status to 'rejected'. ALWAYS requires manual approval (safety list).",
  argsSchema: rejectApplicationArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.applications.reject');
    const { updated_at } = await applicationService.updateStatus(
      args.applicationId,
      'rejected',
      args.reasonText,
      args.reasonCode,
      args.reasonText,
    );
    return {
      ok: true,
      entityKind: 'application',
      entityId: args.applicationId,
      entityLabel: `Application rejected (${args.reasonCode})`,
      // Removes the candidate from the active pipeline — on the safety
      // list. Autonomous mode cannot auto-execute.
      reversible: false,
      message: `Application ${shortId(args.applicationId)} rejected: ${args.reasonCode}.`,
      detail: {
        applicationId: args.applicationId,
        reasonCode: args.reasonCode,
        updated_at,
      },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.interviews.schedule
// ─────────────────────────────────────────────────────────────────────

const scheduleInterviewArgs = z.object({
  applicationId: z.string().min(1),
  round: z.number().int().min(1).max(10),
  scheduledAt: z.string().min(1), // ISO timestamp
  durationMinutes: z.number().int().positive().max(480).optional(),
  location: z.string().optional(),
  interviewerName: z.string().optional(),
  interviewerEmail: z.string().email().optional(),
  notes: z.string().optional(),
  notifyCandidate: z.boolean().default(true),
});

toolRegistry.register<z.infer<typeof scheduleInterviewArgs>, AgentWriteToolResult>({
  name: 'write.interviews.schedule',
  description:
    'Schedule an interview for an application. Inserts a ggj_b2b_interview_events row with status="scheduled" (falls back to "draft" when the outbox trigger table is missing). notifyCandidate=true enqueues the standard confirmation email via the outbox.',
  argsSchema: scheduleInterviewArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.interviews.schedule');
    const event = await interviewEventService.create(
      {
        org_id: ctx.orgId,
        application_id: args.applicationId,
        round: args.round,
        scheduled_at: args.scheduledAt,
        duration_minutes: args.durationMinutes ?? 60,
        location: args.location ?? null,
        interviewer_name: args.interviewerName ?? null,
        interviewer_email: args.interviewerEmail ?? null,
        notes: args.notes ?? null,
      },
      {
        notifyCandidate: args.notifyCandidate,
        createdBy: ctx.userId,
      },
    );
    return {
      ok: true,
      entityKind: 'interview',
      entityId: event.id,
      entityLabel: `Interview R${event.round} @ ${event.scheduled_at ?? '(unscheduled)'}`,
      // Interview can be rescheduled/deleted — reversible. But if
      // notifyCandidate=true we've already queued an outbound email, so
      // treat the whole operation as requiring plan review when
      // notifying; pure internal holds are reversible.
      reversible: args.notifyCandidate !== true,
      message: `Interview R${event.round} scheduled for ${args.scheduledAt}.`,
      detail: {
        interviewId: event.id,
        applicationId: args.applicationId,
        notified: args.notifyCandidate === true,
      },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.interviews.submit_feedback
// ─────────────────────────────────────────────────────────────────────

const submitFeedbackArgs = z.object({
  applicationId: z.string().min(1),
  interviewEventId: z.string().optional(),
  round: z.string().min(1),
  overallScore: z.number().min(0).max(10),
  recommendation: z.enum(['strong_yes', 'yes', 'maybe', 'no', 'strong_no']),
  rubric: z
    .array(z.object({ criterion: z.string(), score: z.number().nullable(), note: z.string() }))
    .default([]),
  notes: z.string().optional(),
  interviewerName: z.string().optional(),
});

toolRegistry.register<z.infer<typeof submitFeedbackArgs>, AgentWriteToolResult>({
  name: 'write.interviews.submit_feedback',
  description:
    'Submit a scorecard for a completed interview (overallScore 0–10 + recommendation + rubric). Writes to ggj_b2b_interview_scorecards via scorecardService.',
  argsSchema: submitFeedbackArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.interviews.submit_feedback');
    const row = await scorecardService.create({
      org_id: ctx.orgId,
      application_id: args.applicationId,
      interview_event_id: args.interviewEventId ?? null,
      round: args.round,
      overall_score: args.overallScore,
      recommendation: args.recommendation,
      rubric: args.rubric,
      notes: args.notes ?? null,
      interviewer_id: ctx.userId,
      interviewer_name: args.interviewerName ?? null,
    });
    return {
      ok: true,
      entityKind: 'interview',
      entityId: row.id,
      entityLabel: `Scorecard R${row.round} · ${row.recommendation ?? 'n/a'}`,
      reversible: true, // scorecards can be updated/deleted via scorecardService
      message: `Feedback submitted (${row.recommendation}, ${row.overall_score}/10).`,
      detail: { scorecardId: row.id, applicationId: row.application_id },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.emails.send
// ─────────────────────────────────────────────────────────────────────

const sendEmailArgs = z.object({
  applicationId: z.string().min(1),
  recipientEmail: z.string().email(),
  recipientName: z.string().optional(),
  subject: z.string().min(1),
  bodyHtml: z.string().min(1),
  templateKey: z.string().default('ad_hoc'),
  nextFollowupAt: z.string().nullable().optional(),
});

toolRegistry.register<z.infer<typeof sendEmailArgs>, AgentWriteToolResult>({
  name: 'write.emails.send',
  description:
    "Create + queue an outbound email on an application thread. Writes to ggj_b2b_email_threads via emailService.createDraft — the existing outbox worker picks it up. ALWAYS requires manual approval (safety list).",
  argsSchema: sendEmailArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.emails.send');
    const thread = await emailService.createDraft({
      org_id: ctx.orgId,
      application_id: args.applicationId,
      sent_by: ctx.userId,
      recipient_email: args.recipientEmail,
      recipient_name: args.recipientName,
      subject: args.subject,
      body_html: args.bodyHtml,
      template_key: args.templateKey,
      next_followup_at: args.nextFollowupAt ?? null,
    });
    return {
      ok: true,
      entityKind: 'email',
      entityId: thread.id,
      entityLabel: `Email → ${args.recipientEmail}`,
      // Sends an external email — never reversible. Safety list.
      reversible: false,
      message: `Email queued to ${args.recipientEmail}: "${args.subject}".`,
      detail: { threadId: thread.id, applicationId: args.applicationId },
    };
  },
});

// ─────────────────────────────────────────────────────────────────────
// write.outreach.sequence_start
// ─────────────────────────────────────────────────────────────────────

const startSequenceArgs = z.object({
  sequenceId: z.string().min(1),
  candidateIds: z.array(z.string().min(1)).min(1).max(100),
});

toolRegistry.register<z.infer<typeof startSequenceArgs>, AgentWriteToolResult>({
  name: 'write.outreach.sequence_start',
  description:
    'Enroll one or more candidates into an existing outreach sequence (ggj_b2b_email_sequences). Each enrollment triggers the sequence worker to queue the first step. ALWAYS requires manual approval (safety list).',
  argsSchema: startSequenceArgs,
  resultSchema: writeResultSchema,
  permissions: ['internal:write'],
  capability: 'write',
  async invoke(ctx, args) {
    assertOrg(undefined, ctx.orgId, 'write.outreach.sequence_start');
    // Lookup the sequence to confirm it belongs to ctx.orgId. The list
    // is per-org so a name lookup would suffice; we call get to hydrate
    // the sequence name for the result label. If the sequence belongs
    // to another org, RLS will return null → throw a friendly error.
    const seq = await emailSequenceService.get(args.sequenceId);
    if (!seq || seq.org_id !== ctx.orgId) {
      throw new Error('Sequence not found or not accessible in this org.');
    }
    await sequenceRecipientsService.enroll(args.sequenceId, args.candidateIds);
    return {
      ok: true,
      entityKind: 'outreach',
      entityId: args.sequenceId,
      entityLabel: `Sequence "${seq.name}" · ${args.candidateIds.length} recipients`,
      // Starts automated outbound emails — never reversible in autonomous
      // mode. Safety list.
      reversible: false,
      message: `Enrolled ${args.candidateIds.length} candidates into "${seq.name}".`,
      detail: { sequenceId: seq.id, count: args.candidateIds.length },
    };
  },
});
